import type { ActivitySurface } from './activityCatalog'

export interface SurfaceDataItem {
  title: string
  meta: string
  status: string
  detail: string
}

export interface SurfaceDataView {
  key: string
  title: string
  summary: string
  items: SurfaceDataItem[]
}

export interface SurfaceDataPayload {
  key: string
  source?: 'static' | 'bot-runtime'
  generatedAt?: number
  headline: string
  health: string
  views: SurfaceDataView[]
  safeguards: string[]
  nextBackend: string[]
}

const payloads: Record<string, SurfaceDataPayload> = {
  home: {
    key: 'home',
    headline: 'Entrada unica para entender o servidor e escolher o proximo fluxo.',
    health: 'Read-only; depende de command registry, guias e manifesto da Activity.',
    views: [
      {
        key: 'start',
        title: 'Primeiro acesso',
        summary: 'Checklist visual para entrar no servidor sem decorar comandos.',
        items: [
          {
            title: 'Ler guia e canais',
            meta: '/server-guide',
            status: 'pronto para API',
            detail: 'Mostra regras, canais certos, cargos visuais e caminhos de participacao.'
          },
          {
            title: 'Escolher fluxo',
            meta: '/commands',
            status: 'catalogado',
            detail: 'Agrupa jogar, perfil, pro CS, inventario, reports e administracao.'
          },
          {
            title: 'Abrir Matchroom',
            meta: '/matchroom',
            status: 'ativo',
            detail: 'Atalho direto para a primeira superficie viva da Activity.'
          }
        ]
      },
      {
        key: 'today',
        title: 'Hoje no servidor',
        summary: 'Fila, partidas, eventos e pendencias que devem virar widgets.',
        items: [
          {
            title: 'PUG disponivel',
            meta: '/fila',
            status: 'backend pendente',
            detail: 'Deve ler fila e disponibilidade com permissao server-side.'
          },
          {
            title: 'Partidas pro',
            meta: '/matches',
            status: 'read-only primeiro',
            detail: 'A agenda pro pode ser exibida sem mutacao de membro.'
          }
        ]
      }
    ],
    safeguards: ['Nao expor IDs crus de canais.', 'Manter comandos como fallback.'],
    nextBackend: ['Command registry JSON', 'Resumo de canais', 'Status de filas']
  },
  profiles: {
    key: 'profiles',
    headline: 'Centro visual para identidade competitiva e historico do jogador.',
    health: 'Read-only inicial; privacidade e avaliacoes exigem usuario autenticado.',
    views: [
      {
        key: 'cards',
        title: 'Cards',
        summary: 'Perfis compactos com rating, funcoes e ultimos sinais.',
        items: [
          {
            title: 'Costa',
            meta: 'IGL · Faceit 8',
            status: 'vinculado',
            detail: 'Passport, stats, ultimas partidas e tags em um so painel.'
          },
          {
            title: 'Vini',
            meta: 'Entry · Premier 16k',
            status: 'vinculado',
            detail: 'Comparacao lado a lado e historico recente ficam em tabs.'
          }
        ]
      },
      {
        key: 'compare',
        title: 'Comparar',
        summary: 'Comparacoes devem sair do texto do chat para tabela visual.',
        items: [
          {
            title: 'K/D e ADR',
            meta: '/compare',
            status: 'read-only',
            detail: 'Ordenar pontos fortes sem expor dados privados de contas.'
          },
          {
            title: 'Historico de ELO',
            meta: '/elo-chart',
            status: 'grafico pendente',
            detail: 'Evolucao temporal deve virar grafico local sem terceiro.'
          }
        ]
      }
    ],
    safeguards: ['Respeitar opt-in de perfil.', 'Nao mostrar contas externas privadas.'],
    nextBackend: ['Profile snapshot', 'Privacy settings', 'Stats timeline']
  },
  leaderboards: {
    key: 'leaderboards',
    headline: 'Rankings filtraveis por fonte, periodo e perfil competitivo.',
    health: 'Read-only; refresh e rebuild ficam admin-gated.',
    views: [
      {
        key: 'rating',
        title: 'Rating',
        summary: 'Top do servidor por origem de rating.',
        items: [
          {
            title: 'Top FACEIT',
            meta: '/ranking plataforma:faceit',
            status: 'read-only',
            detail: 'Tabela com posicao, variacao e ultima atualizacao.'
          },
          {
            title: 'Top Premier',
            meta: '/ranking plataforma:premier',
            status: 'read-only',
            detail: 'Separar origem para nao comparar sinais diferentes sem contexto.'
          }
        ]
      },
      {
        key: 'ops',
        title: 'Operacao',
        summary: 'Acoes que ficam visiveis mas travadas para admins.',
        items: [
          {
            title: 'Atualizar painel',
            meta: '/refresh-leaderboard',
            status: 'admin bloqueado',
            detail: 'Executar somente no bot com auditoria.'
          }
        ]
      }
    ],
    safeguards: ['Nao revelar IDs crus.', 'Admin refresh sempre auditado.'],
    nextBackend: ['Leaderboard snapshot', 'Source filters', 'Refresh status']
  },
  'pro-cs': {
    key: 'pro-cs',
    headline: 'Hub navegavel para jogos profissionais, eventos e patch notes.',
    health: 'Read-only natural; lembretes e syncs continuam admin-gated.',
    views: [
      {
        key: 'matches',
        title: 'Partidas',
        summary: 'Upcoming, live e resultados em tabs ao inves de embeds longos.',
        items: [
          {
            title: 'Ao vivo',
            meta: '/matches live',
            status: 'read-only',
            detail: 'BO3, GGScore, PandaScore e fallback publico ficam normalizados.'
          },
          {
            title: 'Proximas',
            meta: '/matches upcoming',
            status: 'read-only',
            detail: 'Filtros por evento, time e horario local.'
          }
        ]
      },
      {
        key: 'teams',
        title: 'Times',
        summary: 'Busca de times e players com cards laterais.',
        items: [
          {
            title: 'FURIA',
            meta: '/pro team',
            status: 'publico',
            detail: 'Roster, proximas partidas e ultimos resultados.'
          },
          {
            title: 'Patch notes',
            meta: '/patch-notes',
            status: 'publico',
            detail: 'Resumo visual e links oficiais sanitizados.'
          }
        ]
      }
    ],
    safeguards: ['Nao renderizar payload de apostas.', 'Nao imprimir tokens de providers.'],
    nextBackend: ['Pro match snapshot', 'Team/player lookup', 'Patch feed']
  },
  inventory: {
    key: 'inventory',
    headline: 'Inventario, alertas, carteira e loja como painel filtravel.',
    health: 'Read-only primeiro; compras e alertas exigem opt-in e autenticacao.',
    views: [
      {
        key: 'skins',
        title: 'Skins',
        summary: 'Itens ordenaveis por valor, variacao e origem.',
        items: [
          {
            title: 'AK-47 Redline',
            meta: 'BRL estimado',
            status: 'opt-in',
            detail: 'Mostrar faixa de valor e fonte sem expor inventario privado.'
          },
          {
            title: 'AWP Asiimov',
            meta: 'watchlist',
            status: 'alerta ativo',
            detail: 'Alertas devem ser mutacao autenticada no bot.'
          }
        ]
      },
      {
        key: 'shop',
        title: 'Loja',
        summary: 'Economia cosmetica com saldo e compras auditaveis.',
        items: [
          {
            title: 'Badge temporaria',
            meta: '/shop',
            status: 'compra bloqueada',
            detail: 'A compra exige confirmacao server-side e historico.'
          },
          {
            title: 'Carteira',
            meta: '/wallet',
            status: 'read-only',
            detail: 'Saldo pode ser exibido; ajustes ficam admin-only.'
          }
        ]
      }
    ],
    safeguards: ['Inventario so com opt-in.', 'Compras sempre auditadas.'],
    nextBackend: ['Inventory summary', 'Price alerts', 'Wallet/shop snapshot']
  },
  'demo-review': {
    key: 'demo-review',
    headline: 'Historico visual de demos analisadas; upload continua pelo Discord.',
    health: 'Command-first para arquivos; Activity mostra resultado e historico.',
    views: [
      {
        key: 'reports',
        title: 'Relatorios',
        summary: 'Lista de relatorios HTML gerados pelo bot.',
        items: [
          {
            title: 'Inferno 16x13',
            meta: '/demo analisar',
            status: 'fixture validado',
            detail: 'Abrir resumo, rounds decisivos e cards de jogadores.'
          },
          {
            title: 'Mirage treino',
            meta: 'upload command-first',
            status: 'aguardando arquivo',
            detail: 'A Activity nao deve tentar substituir o anexo .dem.'
          }
        ]
      }
    ],
    safeguards: ['Nao aceitar upload direto na Activity agora.', 'Retencao curta para arquivos.'],
    nextBackend: ['Demo report index', 'Report detail JSON']
  },
  matchmaking: {
    key: 'matchmaking',
    headline: 'Disponibilidade, fila, lobbies e sugestoes de grupo em tempo real.',
    health: 'Mutacoes de membro exigem token Activity e checagem de guilda.',
    views: [
      {
        key: 'queue',
        title: 'Fila',
        summary: 'Quem joga hoje, quem entrou na fila e lobbies ativos.',
        items: [
          {
            title: 'Fila casual',
            meta: '/fila',
            status: 'mutacao pendente',
            detail: 'Entrar/sair da fila deve validar membro no servidor.'
          },
          {
            title: 'Lobby 5v5',
            meta: '/lobby',
            status: 'read-only primeiro',
            detail: 'Estado visual pode ser servido antes de permitir join.'
          }
        ]
      },
      {
        key: 'availability',
        title: 'Disponibilidade',
        summary: 'Calendario leve para montar partidas.',
        items: [
          {
            title: 'Hoje 21h',
            meta: '/jogar-hoje',
            status: 'membro',
            detail: 'Marcacao de horario precisa de permissao e rate limit.'
          }
        ]
      }
    ],
    safeguards: ['Sem auto-ping em massa.', 'Rate limit por membro.'],
    nextBackend: ['Queue state', 'Availability slots', 'Party suggestions']
  },
  teams: {
    key: 'teams',
    headline: 'Rosters, convites e captain tools com confirmacoes claras.',
    health: 'Read-only rosters primeiro; convites depois com auditoria.',
    views: [
      {
        key: 'rosters',
        title: 'Rosters',
        summary: 'Times internos, jogadores e funcoes.',
        items: [
          {
            title: 'Striker Alpha',
            meta: '5 membros',
            status: 'ativo',
            detail: 'Card de time com captain, funcoes e busca de scrim.'
          },
          {
            title: 'Free agents',
            meta: '/procurar-time',
            status: 'read-only',
            detail: 'Jogadores procurando time por funcao e horario.'
          }
        ]
      }
    ],
    safeguards: ['Convites precisam de confirmacao.', 'Transferencia de captain e sensivel.'],
    nextBackend: ['Team roster snapshot', 'Invite state', 'Captain guard']
  },
  scrims: {
    key: 'scrims',
    headline: 'Agenda de scrims, coaching e treino organizada por status.',
    health: 'Criacao/fechamento sao mutacoes; leitura pode vir primeiro.',
    views: [
      {
        key: 'open',
        title: 'Abertas',
        summary: 'Pedidos de treino e busca de adversario.',
        items: [
          {
            title: 'Scrim BO3',
            meta: '/scrim',
            status: 'aberta',
            detail: 'Mostrar mapa, horario, nivel e contato sem IDs crus.'
          },
          {
            title: 'Review AWP',
            meta: '/coach',
            status: 'fila coaching',
            detail: 'Coaches podem assumir depois de autenticacao.'
          }
        ]
      }
    ],
    safeguards: ['Nao expor canais privados.', 'Fechamento sempre auditado.'],
    nextBackend: ['Scrim queue', 'Coach queue', 'Status transitions']
  },
  tournaments: {
    key: 'tournaments',
    headline: 'Brackets, check-in e placares em uma tela de competicao.',
    health: 'Read-only bracket primeiro; check-in e iniciar torneio ficam guardados.',
    views: [
      {
        key: 'bracket',
        title: 'Bracket',
        summary: 'Chave e partidas pendentes.',
        items: [
          {
            title: 'Semifinal A',
            meta: '/tournament',
            status: 'aguardando check-in',
            detail: 'Times, mapa e horario em formato de tabela.'
          },
          {
            title: 'Report de placar',
            meta: '/match',
            status: 'confirmacao',
            detail: 'Placar precisa de dupla confirmacao ou admin.'
          }
        ]
      }
    ],
    safeguards: ['Check-in precisa usuario autenticado.', 'Inicio de torneio admin-only.'],
    nextBackend: ['Tournament snapshot', 'Match confirmations', 'Bracket state']
  },
  pickems: {
    key: 'pickems',
    headline: 'Palpites, votos e ranking em cards claros.',
    health: 'Voto e fechamento precisam backend; historico pode ser read-only.',
    views: [
      {
        key: 'open',
        title: 'Abertos',
        summary: 'Pickems ativos e historico de votos.',
        items: [
          {
            title: 'FURIA x NAVI',
            meta: '/pickem',
            status: 'votacao aberta',
            detail: 'Votar A/B exige autenticacao e bloqueio de voto duplicado.'
          },
          {
            title: 'Ranking semanal',
            meta: 'historico',
            status: 'read-only',
            detail: 'Pontuacao e acertos podem ser exibidos sem mutacao.'
          }
        ]
      }
    ],
    safeguards: ['Um voto por membro.', 'Fechar pickem admin-only.'],
    nextBackend: ['Open pickems', 'Vote endpoint', 'Pickem ranking']
  },
  clips: {
    key: 'clips',
    headline: 'Clip of the Week visual, com envio e votacao auditados.',
    health: 'Lista read-only primeiro; envio/voto com validacao de URL e membro.',
    views: [
      {
        key: 'week',
        title: 'Semana',
        summary: 'Clips enviados e vencedor parcial.',
        items: [
          {
            title: '1v3 Mirage',
            meta: '/clip',
            status: 'votavel',
            detail: 'Preview, autor e votos sem mass mention.'
          },
          {
            title: 'Historico',
            meta: 'Hall of Fame',
            status: 'read-only',
            detail: 'Vencedores devem ser navegaveis pela Activity.'
          }
        ]
      }
    ],
    safeguards: ['Sanitizar URLs.', 'Voto autenticado e rate-limited.'],
    nextBackend: ['Clip index', 'Vote state', 'Winner history']
  },
  coaching: {
    key: 'coaching',
    headline: 'Fila de coaching com perfis, pedidos e status.',
    health: 'Read-only primeiro; assumir pedido exige membro coach autorizado.',
    views: [
      {
        key: 'requests',
        title: 'Pedidos',
        summary: 'Pedidos por area e prioridade.',
        items: [
          {
            title: 'Review demo',
            meta: 'Inferno CT side',
            status: 'aberto',
            detail: 'Coach ve area e resumo sem receber arquivo privado direto.'
          },
          {
            title: 'Perfil coach',
            meta: '/coach',
            status: 'ativo',
            detail: 'Areas de foco e disponibilidade.'
          }
        ]
      }
    ],
    safeguards: ['Nao expor comentarios privados.', 'Assumir pedido precisa permissao.'],
    nextBackend: ['Coach profiles', 'Request queue', 'Status mutation']
  },
  seasons: {
    key: 'seasons',
    headline: 'Temporada, awards e magazine em uma linha do tempo.',
    health: 'Read-only natural; publicacao de revista fica admin-gated.',
    views: [
      {
        key: 'month',
        title: 'Mes atual',
        summary: 'Pontos, eventos e awards.',
        items: [
          {
            title: 'Season Maio',
            meta: '/season',
            status: 'ativo',
            detail: 'Ranking, progresso e proximos eventos.'
          },
          {
            title: 'Weekly awards',
            meta: '/awards',
            status: 'read-only',
            detail: 'Premios e criterio visivel.'
          }
        ]
      }
    ],
    safeguards: ['Publicacao admin-only.', 'Sem comentarios privados em magazine.'],
    nextBackend: ['Season snapshot', 'Awards history', 'Magazine index']
  },
  'hall-of-fame': {
    key: 'hall-of-fame',
    headline: 'Memoria permanente do servidor com filtros por categoria.',
    health: 'Read-only primeiro; sugestoes e marcos precisam moderacao.',
    views: [
      {
        key: 'highlights',
        title: 'Destaques',
        summary: 'Clips, rivalidades, temporadas e duelos.',
        items: [
          {
            title: 'Clip eterno',
            meta: '/hof',
            status: 'publico',
            detail: 'Referencia visual para momentos importantes.'
          },
          {
            title: 'Rivalidade saudavel',
            meta: '/rivals',
            status: 'moderado',
            detail: 'Evitar provocacao abusiva; manter contexto positivo.'
          }
        ]
      }
    ],
    safeguards: ['Moderacao em duelos.', 'Sem conteudo toxico persistente.'],
    nextBackend: ['HOF index', 'Category filters', 'Suggestion queue']
  },
  economy: {
    key: 'economy',
    headline: 'Carteira, loja e historico de pontos com trilha auditavel.',
    health: 'Saldo read-only primeiro; compras e ajustes com confirmacao.',
    views: [
      {
        key: 'wallet',
        title: 'Carteira',
        summary: 'Saldo, itens e extrato.',
        items: [
          {
            title: 'Saldo cosmetico',
            meta: '/wallet',
            status: 'read-only',
            detail: 'Mostrar pontos e itens sem permitir ajuste no cliente.'
          },
          {
            title: 'Loja',
            meta: '/shop',
            status: 'compra bloqueada',
            detail: 'Compra precisa confirmacao server-side.'
          }
        ]
      }
    ],
    safeguards: ['Ajuste de pontos admin-only.', 'Historico imutavel para auditoria.'],
    nextBackend: ['Wallet snapshot', 'Shop catalog', 'Purchase endpoint']
  },
  reports: {
    key: 'reports',
    headline: 'Sala de reports como arquivo navegavel de HTML deterministico.',
    health: 'Read-only preview primeiro; geracao e templates admin-gated.',
    views: [
      {
        key: 'packs',
        title: 'Packs',
        summary: 'Reports gerados e inventario de comandos.',
        items: [
          {
            title: 'Command inventory',
            meta: '/commands',
            status: 'HTML pronto',
            detail: 'Abrir report HTML sem expor IDs crus.'
          },
          {
            title: 'Roadmap report',
            meta: '/roadmap',
            status: 'HTML pronto',
            detail: 'Status de RMs e gates em tabela.'
          }
        ]
      }
    ],
    safeguards: ['Reports sem AI commentary.', 'Sem IDs privados por padrao.'],
    nextBackend: ['Report index', 'HTML attachment preview', 'Admin generation']
  },
  roadmap: {
    key: 'roadmap',
    headline: 'Roadmap com status, evidencias e proximos bloqueios.',
    health: 'Read-only; publicacao de update permanece via script/bot.',
    views: [
      {
        key: 'status',
        title: 'Status',
        summary: 'RMs concluidas, bloqueadas e ready-for-live-proof.',
        items: [
          {
            title: 'RM-042 Activity',
            meta: 'Concluida + expansao',
            status: 'em evolucao',
            detail: 'Shell e deploy prontos; novas APIs seguem como proximos passos.'
          },
          {
            title: 'RM-011 Twitch',
            meta: 'live proof',
            status: 'aguarda prova',
            detail: 'Manter separado de Activity para nao superestimar.'
          }
        ]
      }
    ],
    safeguards: ['Nao marcar completo sem prova.', 'Separar bloqueado, parcial e pronto.'],
    nextBackend: ['Roadmap card index', 'Evidence links', 'Status filters']
  },
  'server-guide': {
    key: 'server-guide',
    headline: 'Guia vivo de canais, cargos e como participar.',
    health: 'Read-only e publico; admin updates ficam por comando.',
    views: [
      {
        key: 'channels',
        title: 'Canais',
        summary: 'Onde usar cada fluxo.',
        items: [
          {
            title: 'como-usar',
            meta: '/matches e /matchroom',
            status: 'operacional',
            detail: 'Comandos de uso e provas humanas acontecem aqui.'
          },
          {
            title: 'commands',
            meta: '/eventos-pro',
            status: 'operacional',
            detail: 'Comandos e previews que nao poluem canais sociais.'
          }
        ]
      }
    ],
    safeguards: ['Nao mostrar IDs crus.', 'Canais privados ocultos por padrao.'],
    nextBackend: ['Channel blueprint', 'Role guide', 'Onboarding steps']
  },
  brand: {
    key: 'brand',
    headline: 'Marca, logo, cores e assets em uma vitrine operacional.',
    health: 'Read-only; auditoria de badges admin-gated.',
    views: [
      {
        key: 'assets',
        title: 'Assets',
        summary: 'Logo oficial, icons e role icons.',
        items: [
          {
            title: 'Logo oficial',
            meta: 'assets/brand/striker-arena-logo.png',
            status: 'aprovado',
            detail: 'Fonte canonica para Discord app, bot e servidor.'
          },
          {
            title: 'Tokens',
            meta: 'DESIGN.md',
            status: 'ativo',
            detail: 'Carbon, graphite, white e Striker orange.'
          }
        ]
      }
    ],
    safeguards: ['Nao recriar logo sem aprovacao.', 'Badge audit sem alterar cargos direto.'],
    nextBackend: ['Asset manifest', 'Role icon inventory', 'Badge audit preview']
  },
  'source-servers': {
    key: 'source-servers',
    headline: 'Lista operacional dos servidores CS salvos e consultas seguras.',
    health: 'Read-only primeiro; salvar/remover servidores continua admin-gated.',
    views: [
      {
        key: 'saved',
        title: 'Salvos',
        summary: 'Servidores conhecidos, aliases e status recente.',
        items: [
          {
            title: 'Retake principal',
            meta: '/server list',
            status: 'catalogado',
            detail: 'Mostrar alias, modo e ultima consulta sem revelar IP privado por padrao.'
          },
          {
            title: 'Query manual',
            meta: '/server query',
            status: 'read-only',
            detail: 'Resultado de jogadores/mapa pode virar card filtravel.'
          }
        ]
      }
    ],
    safeguards: ['Nao expor endereco privado sem permissao.', 'Salvar/remover admin-only.'],
    nextBackend: ['Saved servers snapshot', 'Source query proxy', 'Admin save guard']
  },
  twitch: {
    key: 'twitch',
    headline: 'Canais Twitch monitorados com status live e alertas visuais.',
    health: 'Read-only natural; sync manual e config permanecem admin-gated.',
    views: [
      {
        key: 'live',
        title: 'Ao vivo',
        summary: 'Streams do servidor e sinais do provider.',
        items: [
          {
            title: 'Striker Arena watch',
            meta: '/twitch',
            status: 'provider configurado',
            detail: 'Exibir canal, jogo, titulo sanitizado e quando ficou live.'
          },
          {
            title: 'Fallback API',
            meta: 'Twitch OAuth',
            status: 'credencial externa',
            detail: 'Nao mostrar client secret; surface le somente snapshot publico.'
          }
        ]
      }
    ],
    safeguards: ['Nao expor tokens Twitch.', 'Evitar auto-ping repetido de lives.'],
    nextBackend: ['Twitch live snapshot', 'Channel filters', 'Alert delivery state']
  },
  soundboard: {
    key: 'soundboard',
    headline: 'Pack oficial, regras e permissoes da soundboard em formato navegavel.',
    health: 'Read-only; tocar/gerenciar sons deve continuar no Discord com permissao.',
    views: [
      {
        key: 'pack',
        title: 'Pack',
        summary: 'Sons oficiais e momentos recomendados de uso.',
        items: [
          {
            title: 'Entrada de PUG',
            meta: '/soundboard pack',
            status: 'oficial',
            detail: 'Descricao curta do momento sem arquivo bruto na Activity.'
          },
          {
            title: 'Regras de uso',
            meta: '/soundboard rules',
            status: 'moderado',
            detail: 'Permissoes, cooldowns e limites para evitar spam em voz.'
          }
        ]
      }
    ],
    safeguards: ['Nao tocar audio sem acao explicita no Discord.', 'Moderacao e cooldowns visiveis.'],
    nextBackend: ['Soundboard manifest', 'Permission snapshot', 'Usage policy state']
  },
  admin: {
    key: 'admin',
    headline: 'Operacoes sensiveis visiveis, mas executadas so com confirmacao.',
    health: 'Admin-last: leitura de saude antes de qualquer mutacao.',
    views: [
      {
        key: 'health',
        title: 'Saude',
        summary: 'Cron, DB, backups e provider probes.',
        items: [
          {
            title: 'DB stats',
            meta: '/db-stats',
            status: 'read-only',
            detail: 'Pode exibir contagens e ultima migracao sem dados pessoais.'
          },
          {
            title: 'Reset member',
            meta: '/reset-user',
            status: 'destrutivo bloqueado',
            detail: 'Precisa confirmacao dupla, permissao e audit trail.'
          }
        ]
      }
    ],
    safeguards: ['Nada destrutivo sem confirmacao.', 'Sem tokens, IDs privados ou stack traces.'],
    nextBackend: ['Admin health snapshot', 'Confirmation flow', 'Audit log']
  }
}

export function surfaceDataFor(surface: ActivitySurface): SurfaceDataPayload {
  return payloads[surface.key] ?? {
    key: surface.key,
    headline: surface.description,
    health: 'Surface catalogada; backend especifico ainda pendente.',
    views: [
      {
        key: 'summary',
        title: 'Resumo',
        summary: surface.description,
        items: surface.commands.map((command) => ({
          title: command,
          meta: surface.maturity,
          status: 'catalogado',
          detail: 'Fluxo mapeado para migracao incremental de comando para Activity.'
        }))
      }
    ],
    safeguards: ['Manter comandos como fallback.', 'Nao adicionar mutacoes sem guarda server-side.'],
    nextBackend: ['Snapshot read-only', 'Permissoes server-side']
  }
}
